import { FC, useEffect, useRef } from 'react';
import { GetUsersInLocationsResponse } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { TaskCenterTypes } from '@frontend/api-task-center';
import { useTranslation } from '@frontend/i18n';
import { Citation, useTaskCenterStore } from '@frontend/task-center-scope';
import { FormValues, Text, TextareaField, UseFormReturnType } from '@frontend/design-system';
import { TASK_CENTER_CONSTANTS } from '../../constants';
import { CreateTaskFormKeys, CreateTaskFormType } from '../../types';
import { DatePickerDialog, LocationPicker, PersonPicker, Switcher } from '../common';
import { PatientPicker } from '../common/fields/patient-picker';
import { TaskCitation } from './citation/task-citation';
import {
  bottomBorder,
  descFieldStyle,
  formStyle,
  fieldContainerStyle,
  titleFieldStyle,
  getDescFieldHelperTextStyle,
} from './create-task.styles';

const EmptyArrayResponse: any[] = [];

interface TaskFormProps {
  formProps: UseFormReturnType<CreateTaskFormType, CreateTaskFormKeys>;
  selectedTask: TaskCenterTypes.Task | null;
  source: Citation;
  trackingIdSet: Record<string, string>;
  patient: Person | null;
  setPatient: (patient: Person | null) => void;
  isEditing: boolean;
  selectedLocation: string;
  assignees?: GetUsersInLocationsResponse;
  fetchingPatients: boolean;
  formValues: FormValues<CreateTaskFormType>;
  dueDateHasTime: boolean;
  setDueDateHasTime: (hasTime: boolean) => void;
}

const TaskForm: FC<TaskFormProps> = ({
  formProps,
  selectedTask,
  source,
  trackingIdSet,
  patient,
  setPatient,
  isEditing,
  selectedLocation,
  assignees,
  fetchingPatients,
  formValues,
  dueDateHasTime,
  setDueDateHasTime,
}) => {
  const { t } = useTranslation('task-center');
  const { locationsData } = useTaskCenterStore(['locationsData', 'taskMode']);

  const titleFieldRef = useRef<HTMLDivElement>(null);

  const titleLength = formProps.getFieldProps('title').value.length;
  const descriptionLength = formProps.getFieldProps('description').value.length;

  useEffect(() => {
    if (!isEditing) {
      setTimeout(() => {
        titleFieldRef.current?.querySelector('textarea')?.focus();
      }, 100);
    }
  }, [isEditing]);

  const renderTitleFieldHelperText = () => {
    const { touched, error } = formProps.getFieldProps('title');

    if (touched && error) {
      return (
        <Text color='error' size='small'>
          {error}
        </Text>
      );
    }

    if (titleLength > TASK_CENTER_CONSTANTS.TITLE_WARNING_LIMIT) {
      return (
        <Text color='subdued' textAlign='right' size='small'>
          {`${titleLength}/${TASK_CENTER_CONSTANTS.TITLE_LIMIT}`}
        </Text>
      );
    }

    return null;
  };

  return (
    <form {...formProps.formProps} css={formStyle}>
      <div css={bottomBorder}>
        <TextareaField
          {...formProps.getFieldProps('title')}
          label={null}
          ref={titleFieldRef}
          autoGrow={[1, 20]}
          css={titleFieldStyle}
          maxLength={TASK_CENTER_CONSTANTS.TITLE_LIMIT}
          className='create-task-title'
          containerCss={fieldContainerStyle}
          onChange={(e) => {
            const event = e as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
            const sanitized = event.target.value.replace(/[^\S ]/g, '');
            formProps.getFieldProps('title').onChange({ name: 'title', value: sanitized });
          }}
          hideErrorText
        />
        {renderTitleFieldHelperText()}
        <TaskCitation
          source={selectedTask?.source}
          sourceId={selectedTask?.sourceId}
          citation={source}
          // Task always have a locationId with it
          locationId={selectedTask?.locationId ?? ''}
          taskId={selectedTask?.id ?? ''}
        />
        <TextareaField
          {...formProps.getFieldProps('description')}
          label={null}
          css={descFieldStyle}
          className='create-task-description'
          containerCss={fieldContainerStyle}
          maxLength={TASK_CENTER_CONSTANTS.DESCRIPTION_LIMIT}
          autoGrow={[2, 15]}
        />
        <Text
          size='small'
          color='subdued'
          textAlign='right'
          css={getDescFieldHelperTextStyle(descriptionLength)}
        >{`${descriptionLength}/${TASK_CENTER_CONSTANTS.DESCRIPTION_LIMIT}`}</Text>
      </div>
      {locationsData.length > 1 && (
        <LocationPicker
          data={locationsData}
          label={t('Location')}
          placeholder={formProps.getFieldProps('locationId').placeholder ?? ''}
          value={formProps.values.locationId ?? ''}
          onSelect={(value: string) => {
            formProps.seedValues({ ...formProps.values, locationId: value, patient: undefined, assignee: undefined });
            setPatient(null);
          }}
          touched={formProps.getFieldProps('locationId').touched}
          error={formProps.getFieldProps('locationId').error}
          trackingIds={{
            chipButton: trackingIdSet.locationDropdown,
            removeButton: trackingIdSet.removeLocationValue,
          }}
          isEditing={isEditing}
        />
      )}
      <PatientPicker
        label={t('Patient')}
        icon='user'
        placeholder={formProps.getFieldProps('patient').placeholder ?? ''}
        buttonIconName='user'
        isRemovable={!isEditing}
        onChange={(patient) => {
          formProps.seedValues({ ...formProps.values, patient: patient?.personId ?? '' });
          setPatient(patient);
        }}
        value={patient}
        trackingIds={{
          chipButton: trackingIdSet.patientDropdown,
          removeButton: trackingIdSet.removePatientValue,
        }}
        isEditing={isEditing}
        locationId={selectedLocation}
        disabled={!selectedLocation}
      />
      <DatePickerDialog
        label={t('Due Date & Time')}
        placeholder={formProps.getFieldProps('dueDate').placeholder ?? ''}
        onChange={(value) => formProps.seedValues({ ...formProps.values, dueDate: value })}
        value={formProps.getFieldProps('dueDate').value}
        trackingIds={{
          chipButton: trackingIdSet.dueDateDropdown,
          removeButton: trackingIdSet.removeDueDateValue,
        }}
        isRemoveable={!isEditing}
        isEditing={isEditing}
        selectedLocationId={formValues.locationId}
        dueDateHasTime={dueDateHasTime}
        setDueDateHasTime={setDueDateHasTime}
        focused={formProps.getFieldProps('dueDate').touched}
      />

      <PersonPicker
        data={assignees?.users ?? EmptyArrayResponse}
        label={t('Assignee')}
        icon='patient'
        placeholder={formProps.getFieldProps('assignee').placeholder ?? ''}
        buttonIconName='patient'
        onChange={(value) => formProps.seedValues({ ...formProps.values, assignee: value })}
        value={formProps.getFieldProps('assignee').value}
        trackingIds={{
          chipButton: trackingIdSet.assigneeDropdown,
          removeButton: trackingIdSet.removeAssigneeValue,
        }}
        isRemovable={!isEditing}
        isEditing={isEditing}
        disabled={fetchingPatients || !assignees?.users?.length}
      />

      <Switcher
        type='status'
        icon='pending'
        label={t('Status')}
        placeholder={formProps.getFieldProps('status').placeholder ?? ''}
        buttonIconName='pending'
        onChange={(value: TaskCenterTypes.TaskStatus | '') =>
          formProps.seedValues({ ...formProps.values, status: value })
        }
        value={formProps.getFieldProps('status').value as TaskCenterTypes.TaskStatus}
        isEditing={isEditing}
        isRemovable={false}
        trackingIds={{ chipButton: trackingIdSet.statusDropdown }}
      />
      <Switcher
        type='priority'
        icon='bar-chart'
        label={t('Priority')}
        placeholder={formProps.getFieldProps('priority').placeholder ?? ''}
        buttonIconName='no-priority'
        onChange={(value: TaskCenterTypes.PriorityType | '') =>
          formProps.seedValues({ ...formProps.values, priority: value })
        }
        value={formProps.getFieldProps('priority').value as unknown as TaskCenterTypes.PriorityType}
        trackingIds={{
          chipButton: trackingIdSet.priorityDropdown,
          removeButton: trackingIdSet.removePriorityValue,
        }}
        statusValue={formProps.values.status as TaskCenterTypes.TaskStatus}
        onClickRemove={() => formProps.seedValues({ ...formProps.values, priority: '' })}
        isEditing={isEditing}
        isRemovable={!isEditing}
      />
    </form>
  );
};

export default TaskForm;
